<template>
  <div>
    <div class="row">
      <div class="col">
        {{type}}
        <component
            v-bind:is="currentInputComponent"
            :type="type"
            :level="level"
            :param="param"
            :parameter="parameter"
            :config="config"
            :value="value"
            :selected-user-id="selectedUserId"
            :application="application"
            :has-permision-save="hasPermissionSave"
            @input="input"
        />
      </div>
    </div>
    <br>
    <div
        class="row"
        v-if="isSavingConfigurations"
    >
      <div class="col">
        <b-spinner
            class="mr-1"
            variant="primary"
        />
        Guardando
      </div>
    </div>
    <div
        class="row"
        v-if="!isSavingConfigurations && hasPermissionSave"
    >
      <div class="col">
        <b-button
            variant="primary"
            @click="saveParameter()"
            v-if="environmentCanSave(level)"
            :disabled="!newValue"
        >
          Guardar Configuracion
        </b-button>
        <b-button
            variant="outline-secondary"
            @click="migrateParameter()"
        >
          Migrar Configuración
        </b-button>
      </div>
    </div>
    <b-modal
        id="migration-modal"
        ref="migration"
        title="Migrar"
        hide-footer
        no-close-on-backdrop
    >
      <div class="row mb-5">
        <div class="col-md-12 mt-3">
          <label>Ambiente destino</label>
          <v-select
              v-model="selectedAmbients"
              multiple
              label="title"
              :options="ambientsOptions"
          />
        </div>
        <div class="col-md-12 mt-3">
          <label>Niveles</label>
          <v-select
              v-model="selectedLevels"
              label="id"
              :options="levels"
          >
            <template #option="{ id }">
              <span> {{ id |capitalize() }}</span>
            </template>
          </v-select>
        </div>
        <div class="col-md-12 mt-3">
          <div style="float: right">
            <b-spinner
                class="mr-1"
                variant="primary"
                v-if="sendingMigration"
            />
            <vs-button
                class="action"
                color="primary"
                type="border"
                icon="save"
                @click="sendMigrateParameter"
                v-if="!sendingMigration"
            >
              Migrar
            </vs-button>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { BButton, BModal } from 'bootstrap-vue';

import ConfigInputSwitch
from '@/febos/global/empresas/componentes/configs/inputs/ConfigInputSwitch';
import ConfigInputOption
from '@/febos/global/empresas/componentes/configs/inputs/ConfigInputOption';
import ConfigInputText from '@/febos/global/empresas/componentes/configs/inputs/ConfigInputText';
import ConfigInputNumber
from '@/febos/global/empresas/componentes/configs/inputs/ConfigInputNumber';
import ConfigInputS3Html
from '@/febos/global/empresas/componentes/configs/inputs/ConfigInputS3Html';
import { ioMigrateConfiguration } from '@/febos/servicios/api/herramientas.api';
import ConfigInputS3File
from '@/febos/global/empresas/componentes/configs/inputs/ConfigInputS3File';
import ConfigInputS3Text
from '@/febos/global/empresas/componentes/configs/inputs/ConfigInputS3Text';
import ConfigInputS3Xsl
from '@/febos/global/empresas/componentes/configs/inputs/ConfigInputS3Xsl';
import ConfigInputS3Json
from '@/febos/global/empresas/componentes/configs/inputs/ConfigInputS3Json';
import ConfigInputS3Image
from '@/febos/global/empresas/componentes/configs/inputs/ConfigInputS3Image';
import ConfigInputTextLarge
from '@/febos/global/empresas/componentes/configs/inputs/ConfigInputTextLarge';
import ConfigInputSet
from '@/febos/global/empresas/componentes/configs/inputs/ConfigInputSet';
import ConfigInputTextSignatureTemplate
from '@/febos/global/empresas/componentes/configs/inputs/ConfigInputTextSignatureTemplate';

const posiblesAmbientes = [
  { title: 'Desarrollo', value: 'desarrollo' },
  { title: 'Pruebas', value: 'pruebas' },
  { title: 'Certificación', value: 'certificacion' },
  { title: 'Produccion', value: 'produccion' }
];
export default {
  name: 'ConfigInputWrapper',
  components: {
    ConfigInputSwitch,
    ConfigInputText,
    ConfigInputS3Json,
    ConfigInputNumber,
    ConfigInputTextLarge,
    'config-input-set': ConfigInputTextLarge,
    ConfigInputS3Html,
    ConfigInputOption,
    ConfigInputS3File,
    ConfigInputS3Text,
    ConfigInputS3Xsl,
    ConfigInputSet,
    ConfigInputS3Image,
    ConfigInputTextSignatureTemplate,
    BButton,
    BModal
  },
  data() {
    return {
      defaultType: 'text',
      types: [
        'switch',
        'set',
        'text',
        'number',
        'text-large',
        's3-html',
        's3-text',
        's3-xsl',
        's3-file',
        's3-image',
        's3-json',
        'set',
        'option',
        'text-signature-template'
      ],
      selectedAmbients: [],
      selectedLevels: undefined,
      newValue: '',
      levelsPermissions: {},
      hasPermissionSave: false,
      sendingMigration: false
    };
  },
  mounted() {
    this.levelsPermissions = this.levels.reduce((obj, item) => ({
      ...obj,
      ...{ [item.id]: item }
    }), {});
    this.hasPermissionSave = this.hasPermissionLevel();
  },
  computed: {
    ...mapGetters('Empresas', [
      'isSavingConfigurations'
    ]),
    ...mapGetters('Usuario', ['hasPermission']),
    currentInputComponent() {
      if (this.types.includes(this.type)) {
        return `config-input-${this.type.toLowerCase()}`;
      }
      return `config-input-${this.defaultType.toLowerCase()}`;
    },
    ambientsOptions() {
      return posiblesAmbientes.filter((ambient) => ambient.value !== process.env.VUE_APP_AMBIENTE);
    },
    environmentCanSave() {
      return (level) => {
        if (level === 'global' || level === 'portal' || level === 'app') {
          return process.env.VUE_APP_AMBIENTE === 'desarrollo';
        }
        return true;
      };
    }
  },
  methods: {
    ...mapActions('Empresas', [
      'saveConfiguration'
    ]),
    hasPermissionLevel() {
      if (this.levelsPermissions[this.level]) {
        if (!this.levelsPermissions[this.level].permissions) {
          return true;
        }
        if (this.hasPermission(this.levelsPermissions[this.level].permissions)) {
          return true;
        }
      }
      return false;
    },
    input(newValue) {
      this.newValue = newValue;
    },
    saveParameter() {
      const params = { origen: this.level, app: this.application };
      if (this.selectedUserId) {
        params.usuarioId = this.selectedUserId;
      }
      this.saveConfiguration({
        params,
        body: { parametroId: this.param, valor: this.newValue }
      });
      this.$emit('saved', this.newValue);
      this.newValue = '';
    },
    migrateParameter() {
      this.selectedAmbients = [];
      this.selectedLevels = { id: this.level };
      this.$refs.migration.show();
    },
    async sendMigrateParameter() {
      if (this.selectedAmbients.length === 0 || !this.selectedLevels) {
        return;
      }
      this.sendingMigration = true;
      try {
        const ambients = this.selectedAmbients.map((ambiente) => ambiente.value);
        const level = this.selectedLevels.id;
        const parameter = this.param;
        await ioMigrateConfiguration({
          parameter, level, ambients, app: this.application, usuarioId: this.selectedUserId
        });
        this.$refs.migration.hide();
      } finally {
        this.sendingMigration = false;
      }
    }
  },
  props: {
    param: {
      type: String,
      required: true
    },
    value: {
      type: String,
      required: false
    },
    level: {
      type: String,
      required: true
    },
    levels: {
      type: Array,
      required: true
    },
    type: {
      type: String,
      required: false,
      default: 'text'
    },
    config: {
      type: Object,
      required: false
    },
    parameter: {
      type: Object,
      required: false
    },
    selectedUserId: {
      type: String,
      required: false
    },
    application: {
      type: String,
      required: true
    }
  }
};
</script>

<style scoped>
.disabled > button {
  cursor: no-drop;
}
</style>
